/**
 * useClinicPromotingInterop — GET /v1/clinics/{cid}/promoting-interop
 *
 * Read endpoint for PI attestations. New in backend v0.4.0. Same shape +
 * semantics as `useClinicImprovementActivities`.
 *
 * Convention (matches v0.3.1 meetings list):
 *   - 200 + empty array when no attestations exist
 *   - 404 only if the clinic itself doesn't exist
 */
import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { PiAttestationListResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface UseClinicPromotingInteropOptions {
  year?: number;
  enabled?: boolean;
}

export function useClinicPromotingInterop(
  clinicId: number,
  options: UseClinicPromotingInteropOptions = {},
) {
  const { year, enabled } = options;
  return useQuery({
    queryKey: queryKeys.pi.byClinic(clinicId, year),
    queryFn: async (): Promise<PiAttestationListResponse> => {
      const params: Record<string, number> = {};
      if (year !== undefined) params.year = year;
      const { data } = await getApiClient().get<PiAttestationListResponse>(
        `/v1/clinics/${clinicId}/promoting-interop`,
        { params: Object.keys(params).length > 0 ? params : undefined },
      );
      return data;
    },
    staleTime: 30_000,
    enabled: enabled ?? Number.isFinite(clinicId),
  });
}
